<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';
	import TrustedChoice from '$lib/PageSections/TrustedChoice/TrustedChoice.svelte';

	import embassy1Image from './images/embassy1.jpeg';
	import embassy2Image from './images/embassy2.jpeg';
	import nantucket1Image from './images/nantucket1.jpeg';
	import nantucket2Image from './images/nantucket2.jpeg';
	import richmond1Image from './images/richmond1.jpeg';
	import richmond2Image from './images/richmond2.jpeg';
	import society1Image from './images/society1.jpeg';
	import society2Image from './images/society2.jpeg';
</script>

<Section class="**:text-left">
	<p>
		Prime location can be the biggest determining factor for a hotel's success,
		but oftentimes, it can also be a drawback for guests. Whether it's the charm
		of a historic district or the energy of a downtown hub, external noise is
		the primary disruptor of the guest experience. For many operators, "solving"
		noise has traditionally meant an endless cycle of room credits, service
		recovery, and defensive responses to negative reviews.
	</p>
	<p>
		Indow Windows offers a definitive exit from that cycle. By creating a
		high-performance acoustic barrier without the cost or disruption of window
		replacement, Indow allows hotels to protect their reputation and their
		revenue simultaneously.
	</p>

	<h2 class="text-left">
		Proven Results Across Diverse Hospitality Environments
	</h2>

	<div class="mb-12">
		<div class="mb-6 grid grid-cols-2 gap-6">
			<Image
				src={nantucket1Image}
				loading="eager"
				class="h-full rounded-sm shadow-md"
			/>
			<Image
				src={nantucket2Image}
				loading="eager"
				class="h-full rounded-sm shadow-md"
			/>
		</div>
		<h3 class="text-lg font-bold text-indow-grey-dark normal-case">
			21 Broad, Nantucket, MA
		</h3>
		<p>
			In a restored 1872 Victorian, original wavy glass windows are a landmark
			requirement, but they offered no protection against alleyway HVAC units
			and street noise.
		</p>
		<div
			class="
     grid grid-cols-1 gap-6
     md:grid-cols-2
   "
		>
			<div>
				<h4 class="font-medium text-indow-grey-dark normal-case">
					The Solution
				</h4>
				<p>
					Acoustic Grade Indow inserts were custom-fitted into the existing
					frames.
				</p>
			</div>
			<div>
				<h4 class="font-medium text-indow-grey-dark normal-case">The Impact</h4>
				<p>
					General Manager Dan Matter reported a night and day difference. The
					inserts were so discreet that staff often forgot they were there
					during routine maintenance.
				</p>
			</div>
		</div>
	</div>

	<div class="mb-12">
		<div class="mb-6 grid grid-cols-2 gap-6">
			<Image
				src={richmond1Image}
				loading="lazy"
				class="h-full rounded-sm shadow-md"
			/>
			<Image
				src={richmond2Image}
				loading="lazy"
				class="h-full rounded-sm shadow-md"
			/>
		</div>
		<h3 class="text-lg font-bold text-indow-grey-dark normal-case">
			The Commonwealth Richmond, VA
		</h3>
		<p>
			Despite a massive renovation, this historic hotel was losing money to
			service recovery, refunding guests disturbed by the early-morning external
			intercoms of the Richmond bus system.
		</p>
		<div
			class="
     grid grid-cols-1 gap-6
     md:grid-cols-2
   "
		>
			<div>
				<h4 class="font-medium text-indow-grey-dark normal-case">
					The Solution
				</h4>
				<p>Quick, hardware-free installation of Indow inserts.</p>
			</div>
			<div>
				<h4 class="font-medium text-indow-grey-dark normal-case">The Impact</h4>
				<p>
					The hotel stopped giving away credits for noise complaints. "It's very
					rare that you can permanently fix any problem in your facility," said
					GM Christina Norton. "Indow has practically fixed it."
				</p>
			</div>
		</div>
	</div>
	<div class="mb-12">
		<div class="mb-6 grid grid-cols-2 gap-6">
			<Image
				src={embassy1Image}
				loading="lazy"
				class="h-full rounded-sm shadow-md"
			/>
			<Image
				src={embassy2Image}
				loading="lazy"
				class="h-full rounded-sm shadow-md"
			/>
		</div>
		<h3 class="text-lg font-bold text-indow-grey-dark normal-case">
			Embassy Suites, Orlando, FL
		</h3>
		<p>
			Surrounded by nightclubs, this 167 room property needed a solution that
			fit into an $8 million renovation without the massive cost or downtime of
			full window replacement.
		</p>
		<div
			class="
     grid grid-cols-1 gap-6
     md:grid-cols-2
   "
		>
			<div>
				<h4 class="font-medium text-indow-grey-dark normal-case">
					The Solution
				</h4>
				<p>
					After a 90 day sleep test by management in the noisiest suite, the
					hotel outfitted 167 windows.
				</p>
			</div>
			<div>
				<h4 class="font-medium text-indow-grey-dark normal-case">The Impact</h4>
				<p>
					The entire installation took just one day. Post-installation, noise
					complaints dropped by an estimated 80%, securing long term guest
					loyalty.
				</p>
			</div>
		</div>
	</div>
	<div class="mb-12">
		<div class="mb-6 grid grid-cols-2 gap-6">
			<Image
				src={society1Image}
				loading="lazy"
				class="h-full rounded-sm shadow-md"
			/>
			<Image
				src={society2Image}
				loading="lazy"
				class="h-full rounded-sm shadow-md"
			/>
		</div>
		<h3 class="text-lg font-bold text-indow-grey-dark normal-case">
			The Society Hotel Portland, OR
		</h3>
		<p>
			This 1881 Victorian-era hotel faced a triple threat: nightlife noise,
			drafty single pane glass, and a goal of achieving LEED certification.
		</p>
		<div
			class="
     grid grid-cols-1 gap-6
     md:grid-cols-2
   "
		>
			<div>
				<h4 class="font-medium text-indow-grey-dark normal-case">
					The Solution
				</h4>
				<p>
					Inserts were installed on the noisy south side, and later expanded to
					the west side to combat thermal loss.
				</p>
			</div>
			<div>
				<h4 class="font-medium text-indow-grey-dark normal-case">The Impact</h4>
				<p>
					The hotel significantly reduced the need for portable electric heaters
					in winter and created a more modern interior feel while keeping the
					historic 8 foot arched windows intact.
				</p>
			</div>
		</div>
	</div>

	<h2 class="text-left">The Indow Advantage for Hotels</h2>
	<ul>
		<li>
			<p>
				<strong>Cost Efficiency:</strong> Achieve window-replacement performance at
				a fraction of the price.
			</p>
		</li>
		<li>
			<p>
				<strong>Zero Downtime:</strong> Fast, non-invasive installation means no lost
				room nights.
			</p>
		</li>
		<li>
			<p>
				<strong>Aesthetic Integrity:</strong> Approved for use in historic landmark
				buildings.
			</p>
		</li>
		<li>
			<p>
				<strong>Proven ROI:</strong> Protect your online reputation and stop the cycle
				of guest refunds.
			</p>
		</li>
	</ul>
</Section>

<TrustedChoice />
<Reviews />
<GetAFreeEstimate />
